// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { OUTCOME_COLOUR, OUTCOME_LABEL, OUTCOME_ORDER, speciesInfo, activityLabel, stateName, stateColour } from '../domain';
import { outcomeCounts, speciesStats, activityStats, stateStats } from '../analysis';
import { donut, horizontalBars, stackedColumns, type StackSeg } from '../components/charts';
import { glossaryTerm } from '../glossary';
import { formatNumber, formatPercent } from '../utils/format';

export function renderOverview(root: HTMLElement, ctx: ViewContext): void {
  const { data } = ctx;
  const n = data.meta.national;
  const c = outcomeCounts(data.incidents);
  const species = speciesStats(data.incidents).filter((s) => s.key !== 'unidentified').slice(0, 8);
  const acts = activityStats(data.incidents).sort((a, b) => b.total - a.total).slice(0, 8);
  const states = stateStats(data.incidents);

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Shark incidents in Australia, ${data.meta.dataFrom}–${data.meta.dataThrough}</h1>
      <p class="view-sub">
        Every recorded shark–human ${glossaryTerm('incident', 'incident')} in the national database — where they happen,
        which sharks, what people were doing, and how they end. The short version: they are rare, and most people survive.
      </p>
    </div>

    <div class="hero">
      <div class="hero-panel">
        <div class="hero-lead">Of every 10 incidents with a known outcome today, about</div>
        <div class="big-ratio">1 in 10</div>
        <div class="hero-lead">proves fatal — down from roughly <strong>1 in 3</strong> before 1940. Recorded incidents
          have risen to record highs, yet a bitten person is far more likely to live than in any earlier era.</div>
        <button class="btn-link" data-goto="time">See the full timeline →</button>
      </div>
      <div class="hero-panel hero-donut">
        <div data-role="donut"></div>
        <div class="legend" data-role="donut-legend"></div>
      </div>
    </div>

    <div class="stat-grid">
      <div class="stat"><div class="stat-label">Recorded incidents</div><div class="stat-value">${formatNumber(n.total)}</div><div class="stat-note">over ${n.total && data.meta.spanYears} years</div></div>
      <div class="stat"><div class="stat-label">Survived</div><div class="stat-value teal">${formatPercent(n.survivePct, 0)}</div><div class="stat-note">of known outcomes</div></div>
      <div class="stat"><div class="stat-label">Fatal</div><div class="stat-value red">${formatNumber(n.fatal)}</div><div class="stat-note">${formatPercent(n.fatalPct, 0)} of known outcomes</div></div>
      <div class="stat"><div class="stat-label">${glossaryTerm('provoked', 'Provoked')}</div><div class="stat-value amber">${formatPercent(n.provokedPct, 0)}</div><div class="stat-note">person initiated contact</div></div>
      <div class="stat"><div class="stat-label">Male victims</div><div class="stat-value">${formatPercent(n.malePct, 0)}</div><div class="stat-note">median age ${n.medianAge ?? '—'}</div></div>
      <div class="stat"><div class="stat-label">Shark species</div><div class="stat-value">${formatNumber(n.species)}</div><div class="stat-note">identified in the record</div></div>
    </div>

    <div class="callout">
      <strong>Three sharks, almost every death.</strong> White, tiger and bull sharks are the only species large enough
      to cause life-threatening injuries — together they account for nearly every fatal bite. Wobbegongs, whalers and
      grey nurse sharks bite people too, but almost always leave only minor wounds.
    </div>

    <div class="grid-2">
      <div class="panel">
        <div class="panel-title">Which sharks</div>
        <div class="panel-sub">Recorded incidents by species. Click a bar for that shark's profile.</div>
        <div class="chart-scroll" data-role="species"></div>
      </div>
      <div class="panel">
        <div class="panel-title">What people were doing</div>
        <div class="panel-sub">Activity at the time of the incident.</div>
        <div class="chart-scroll" data-role="acts"></div>
      </div>
    </div>

    <div class="grid-2">
      <div class="panel">
        <div class="panel-title">Where — by state</div>
        <div class="panel-sub">Raw totals track where people swim. Click a bar for the state, or open the <button class="btn-link inline" data-goto="map">map</button>.</div>
        <div class="chart-scroll" data-role="states"></div>
      </div>
      <div class="panel">
        <div class="panel-title">Across the decades</div>
        <div class="panel-sub">Incidents per decade, coloured by outcome. The red slice barely grows.</div>
        <div class="chart-scroll" data-role="decades"></div>
      </div>
    </div>
  `;

  // Donut
  const donutSlot = root.querySelector('[data-role="donut"]') as HTMLElement;
  donutSlot.appendChild(
    donut(
      OUTCOME_ORDER.map((o) => ({ value: c[o], colour: OUTCOME_COLOUR[o], label: OUTCOME_LABEL[o] })),
      { size: 190, centreLabel: formatNumber(n.total), centreSub: 'incidents' },
    ),
  );
  (root.querySelector('[data-role="donut-legend"]') as HTMLElement).innerHTML = OUTCOME_ORDER.filter((o) => c[o] > 0)
    .map((o) => `<span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${OUTCOME_COLOUR[o]}"></span>${OUTCOME_LABEL[o]} ${formatNumber(c[o])}</span>`)
    .join('');

  // Species bars
  (root.querySelector('[data-role="species"]') as HTMLElement).appendChild(
    horizontalBars(
      species.map((s) => ({
        id: s.key,
        label: s.label,
        value: s.total,
        colour: speciesInfo(s.key).colour,
        tip: `${s.label}: ${formatNumber(s.total)} incidents · ${formatNumber(s.fatal)} fatal (${formatPercent(s.fatalPct, 0)})`,
      })),
      { onClick: (id) => ctx.openSpecies(id), labelWidth: 130 },
    ),
  );

  // Activity bars
  (root.querySelector('[data-role="acts"]') as HTMLElement).appendChild(
    horizontalBars(
      acts.map((a) => ({
        id: a.key,
        label: activityLabel(a.key),
        value: a.total,
        colour: '#2b8994',
        tip: `${activityLabel(a.key)}: ${formatNumber(a.total)} incidents · ${formatPercent(a.fatalPct, 0)} fatal`,
      })),
      { labelWidth: 130 },
    ),
  );

  // State bars
  (root.querySelector('[data-role="states"]') as HTMLElement).appendChild(
    horizontalBars(
      states.map((s) => ({
        id: s.st,
        label: stateName(s.st),
        value: s.total,
        colour: stateColour(s.st),
        tip: `${stateName(s.st)}: ${formatNumber(s.total)} incidents · ${formatNumber(s.fatal)} fatal${s.perMillion != null ? ` · ${s.perMillion.toFixed(0)}/million residents` : ''}`,
      })),
      { onClick: (id) => ctx.openState(id), labelWidth: 150 },
    ),
  );

  // Decade stacked columns
  const segs: StackSeg[] = OUTCOME_ORDER.map((o) => ({ key: o, colour: OUTCOME_COLOUR[o], label: OUTCOME_LABEL[o] }));
  const decs = decadeData(data);
  (root.querySelector('[data-role="decades"]') as HTMLElement).appendChild(
    stackedColumns(decs, segs, { height: 300, onClick: () => ctx.goTo('time') }),
  );

  root.querySelectorAll('[data-goto]').forEach((b) => b.addEventListener('click', () => ctx.goTo(b.getAttribute('data-goto') as never)));
}

function decadeData(data: ViewContext['data']) {
  const map = new Map<number, Record<string, number>>();
  for (const it of data.incidents) {
    if (it.y == null) continue;
    const d = Math.floor(it.y / 10) * 10;
    if (!map.has(d)) map.set(d, { fatal: 0, injured: 0, uninjured: 0, unknown: 0 });
    map.get(d)![it.out]++;
  }
  return [...map.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([d, values]) => ({ id: String(d), label: `${String(d).slice(2)}`, values }));
}
